/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/

import platform from "platform";
import {
  Goal,
  Lesson,
  Resource,
  TopicAttributeRelevance,
  UserStudy,
} from "@ictlearningsciences/pal3-core/types";
import {
  ActivityType,
  LearnerRecord,
  Verb,
} from "@ictlearningsciences/pal3-core/types";
import { CheckLessonTraitReqValid } from "@ictlearningsciences/pal3-core/recommender";

export enum LoadStatus {
  NONE = 0,
  IN_PROGRESS = 1,
  DONE = 2,
  FAILED = 3,
}

export interface LoadingState {
  status: LoadStatus;
  error?: string;
  startedAt?: string;
  endedAt?: string;
  failedAt?: string;
}

export function shuffleArray<T>(arr: T[], len?: number): T[] {
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, len || shuffled.length);
}

export function debugLog(msg: string, error = false): void {
  if (!process.env.DEBUG_LOG) {
    return;
  }
  if (error) {
    console.error(`test: ${msg}`);
  } else {
    console.log(`test: ${msg}`);
  }
}

export function getCompletedLessons(
  records: LearnerRecord[],
  lessons: Record<string, Lesson>
): {
  completedRecords: LearnerRecord[];
  completedLessons: Lesson[];
} {
  const completedLessons: React.SetStateAction<Lesson[]> = [];
  const completedLessonRecords = records.filter((r) => {
    return (
      r.objType === ActivityType.LESSON &&
      (r.verb === Verb.COMPLETED || r.verb === Verb.QUIT)
    );
  });
  const completedRecords = [];
  for (const record of completedLessonRecords) {
    const lesson = lessons[record.objId];
    if (lesson && !completedLessons.includes(lesson)) {
      completedRecords.push(record);
      completedLessons.push(lesson);
    }
  }
  return {
    completedRecords,
    completedLessons,
  };
}

export function GetTotalMins(ms: number): number {
  return ms / (1000 * 60);
}

export function GetTotalDays(ms: number): number {
  return ms / (1000 * 60 * 60 * 24);
}

export const getResourceDuration = (resources: Resource[]): number => {
  let duration = 0;
  resources.forEach((resource) => {
    duration += resource.duration;
  });
  return duration;
};

export function addQueryParam(
  url: string,
  paramName: string,
  paramValue: string
): string {
  const urlObject = new URL(url);
  urlObject.searchParams.append(paramName, paramValue);
  return urlObject.toString();
}

export function buildClassName(...classes: string[]): string {
  return classes.filter((c) => c).join(" ");
}

export function convertSeconds(seconds: number): string | null {
  if (isNaN(seconds) || seconds < 0) return null;

  const date = new Date(seconds * 1000);
  const days = Math.floor(seconds / (3600 * 24));
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const sec = date.getUTCSeconds();

  const timeUnits = [
    { value: days, unit: "d" },
    { value: hours, unit: "hr" },
    { value: minutes, unit: "m" },
    { value: sec, unit: "s" },
  ];

  const formattedTime = timeUnits
    .filter((unit) => unit.value > 0)
    .map(({ value, unit }) => `${value}${unit}`)
    .join(", ");

  return formattedTime || "0 seconds";
}

export function parseResourceTime(time: number): string {
  const seconds = Math.floor((time / 1000) % 60);
  const minutes = Math.floor((time / (1000 * 60)) % 60);
  const hours = Math.floor((time / (1000 * 60 * 60)) % 24);
  const days = Math.floor(time / (1000 * 60 * 60 * 24));
  if (seconds >= 1 && minutes < 1) {
    return seconds == 1 ? seconds + " second" : seconds + " seconds";
  } else if (minutes >= 1 && hours < 1) {
    return minutes == 1 ? minutes + " minute" : minutes + " minutes";
  } else if (hours >= 1 && days < 1) {
    return hours == 1 ? hours + " hour" : hours + " hours";
  } else if (days >= 1) {
    return days == 1 ? days + " day" : days + " days";
  }
  return time == 1 ? time + " millisecond" : time + " milliseconds";
}

export function getLessonType(key: string): string {
  switch (key) {
    case "web":
      return "Reading";
    case "web-active":
      return "Applied";
    case "video":
      return "video";
    case "auto-tutor":
    case "opentutor":
      return "Tutoring";
    case "plan-dialogue":
      return "Planning";
    case "multiple-choice":
      return "Quiz";
    default:
      return "Reading";
  }
}

export function calculateSurveyCompletionTime(
  startedAt: string,
  endedAt: Date
): number {
  const startDate = new Date(startedAt);
  const endDate = endedAt;

  // Calculate the difference in milliseconds
  const differenceMs = endDate.getTime() - startDate.getTime();

  // Convert milliseconds to seconds
  const differenceSeconds = differenceMs / 1000;

  return differenceSeconds;
}

export function getFormattedDateCompletedSurveyDate(
  completedDate: Date | undefined
): string {
  if (!completedDate) return "";
  const currentDate = new Date(completedDate);

  // Get the components of the date
  const month = (currentDate.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based, so we add 1
  const day = currentDate.getDate().toString().padStart(2, "0");
  const year = currentDate.getFullYear().toString();

  // Create the formatted date string
  const formattedDate = `${month}/${day}/${year}`;

  return formattedDate;
}

export function getLessonsForGoal(goal: Goal): Lesson[] {
  const lessons = [];
  for (const focus of goal.focusList) {
    for (const topic of focus.topics) {
      lessons.push(...(topic.lessons || []));
    }
  }
  return lessons;
}

export function lessonsMatchReqs(
  lessons: Lesson[],
  traits: TopicAttributeRelevance[]
): Lesson[] {
  return lessons.filter((l) => {
    if (l.hidden || l.deleted) return false;
    for (const req of l.traitRequirements || []) {
      if (!CheckLessonTraitReqValid(req, traits)) return false;
    }
    return true;
  });
}

export function buildSurveyUrl(base: string, userStudy: UserStudy): string {
  const surveyUrl = new URL(base);
  surveyUrl.searchParams.append(
    "participantid",
    `${userStudy.studyID}_${userStudy.participantID}`
  );
  surveyUrl.searchParams.append("userid", userStudy.participantID);
  surveyUrl.searchParams.append("studyid", userStudy.studyID);
  surveyUrl.searchParams.append("userclient", platform.name || "");
  surveyUrl.searchParams.append("userclientversion", platform.version || "");
  surveyUrl.searchParams.append("pal3app", "web");
  if (process.env.STAGE) {
    surveyUrl.searchParams.append("pal3env", process.env.STAGE);
  }
  return surveyUrl.href;
}
